import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import ReactPhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-input-2/lib/material.css';
import { Button, Dialog, IconButton } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { PhoneConfirmationModalProps } from './types';
import useConfig from '../../../hooks/useConfig';

// Mirrors the front office's e-signature modal (core/ui Modal +
// ESignatureModal + PhoneField), with its Eliot design tokens.
const FO = {
  font: "'Muli', 'Mulish', sans-serif",
  text: '#2b3c4a',
  primary: '#49b1bf',
  primaryHover: '#3a8e99',
  mediumGrey: '#dbdee0',
  defaultGrey: '#a8aeb5',
  shadow: '0 4px 10px 0 rgba(0, 0, 0, 0.2)',
};

const foButton = {
  height: '42px',
  padding: '0 24px',
  borderRadius: '4px',
  fontFamily: FO.font,
  fontSize: '14px',
  fontWeight: 700,
  textTransform: 'none' as const,
  letterSpacing: 'normal',
  boxShadow: 'none',
};

const useStyles = makeStyles({
  backdrop: {
    backgroundColor: 'rgba(43, 60, 74, 0.7)',
  },
  paper: {
    position: 'relative',
    overflow: 'visible',
    // Padding included in the 768px, as in the FO (border-box there).
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '768px',
    margin: '66px',
    padding: '50px',
    borderRadius: '6px',
    boxShadow: FO.shadow,
    fontFamily: FO.font,
    fontSize: '14px',
    fontWeight: 500,
    color: FO.text,
    '& *': {
      fontFamily: FO.font,
    },
  },
  close: {
    position: 'absolute',
    bottom: 'calc(100% + 12px)',
    left: 'calc(100% + 12px)',
    padding: '12px 12px 9px',
    backgroundColor: '#fff',
    boxShadow: FO.shadow,
    color: FO.defaultGrey,
    '&:hover, &:focus': {
      backgroundColor: '#fff',
      color: FO.text,
    },
    '& svg': {
      fontSize: '18px',
    },
  },
  heading: {
    marginBottom: '30px',
  },
  title: {
    margin: '0 0 12px',
    fontSize: '22px',
    fontWeight: 500,
    lineHeight: 1.3,
    color: FO.text,
  },
  subtitle: {
    margin: 0,
    whiteSpace: 'pre-wrap',
  },
  phoneSubtitle: {
    margin: 0,
    paddingBottom: '24px',
    fontWeight: 700,
  },
  phoneRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    marginBottom: '24px',
  },
  phoneField: {
    position: 'relative',
    minWidth: '250px',
    paddingTop: '14px',
    '& .react-tel-input .form-control:hover, & .react-tel-input .form-control:focus, & .react-tel-input .flag-dropdown:hover':
      {
        borderBottomColor: '#1b4867',
        boxShadow: 'none',
      },
    '& .react-tel-input .flag-dropdown, & .react-tel-input .selected-flag': {
      backgroundColor: 'transparent',
    },
  },
  phoneLabel: {
    position: 'absolute',
    top: 0,
    left: '54px',
    fontSize: '11px',
    fontWeight: 500,
    lineHeight: 1.6,
    color: FO.text,
    pointerEvents: 'none',
  },
  updateButton: {
    ...foButton,
    alignSelf: 'center',
    marginLeft: '24px',
    color: FO.primary,
    border: `1px solid ${FO.primary}`,
    backgroundColor: 'transparent',
    '&:hover': {
      color: '#fff',
      backgroundColor: FO.primary,
    },
    '&.Mui-disabled': {
      color: FO.mediumGrey,
      borderColor: FO.mediumGrey,
    },
  },
  updated: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
    gap: '6px',
    marginLeft: '24px',
  },
  updatedIcon: {
    backgroundColor: '#3cb185',
    borderRadius: '25px',
    padding: '3px',
    color: 'white',
    fontSize: '14px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginTop: '55px',
  },
  cancel: {
    ...foButton,
    marginRight: '12px',
    fontWeight: 500,
    color: FO.defaultGrey,
    '&:hover': {
      backgroundColor: 'transparent',
      color: '#8a8f94',
    },
  },
  confirm: {
    ...foButton,
    color: '#fff',
    backgroundColor: FO.primary,
    '&:hover': {
      backgroundColor: FO.primaryHover,
    },
    '&.Mui-disabled': {
      color: '#fff',
      backgroundColor: FO.mediumGrey,
    },
  },
});

/**
 * Asks the signed-in admin to check (and update if needed) the phone number
 * Yousign will send the signing code to, before starting an e-signature.
 */
const PhoneConfirmationModal: FunctionComponent<PhoneConfirmationModalProps> = (
  props
) => {
  const t = useTranslate();
  const classes = useStyles();
  const { defaultCountry, preferredCountries } = useConfig();
  const {
    open,
    title,
    description,
    initialPhone,
    loading,
    handleClose,
    handleUpdatePhone,
    handleConfirm,
    confirmLabel = 'fields.nda.modal.validate_button',
  } = props;
  const [phone, setPhone] = useState(initialPhone || '');
  const [updating, setUpdating] = useState(false);
  const [updatedAt, setUpdatedAt] = useState('');

  useEffect(() => {
    if (open) setPhone(initialPhone || '');
  }, [open, initialPhone]);

  useEffect(() => {
    if (!open) setUpdatedAt('');
  }, [open]);

  const valid = isValidPhoneNumber(phone);

  const updatePhone = async () => {
    setUpdating(true);

    try {
      await handleUpdatePhone(phone);
      setUpdatedAt(
        new Date().toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ className: classes.paper }}
      BackdropProps={{ className: classes.backdrop }}
    >
      <IconButton className={classes.close} onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
      <div className={classes.heading}>
        <h1 className={classes.title}>{t(title)}</h1>
        <p className={classes.subtitle}>{t(description)}</p>
      </div>
      <p className={classes.phoneSubtitle}>
        {t('esignature.modal.phone.subtitle')}
      </p>
      <div className={classes.phoneRow}>
        <div className={classes.phoneField}>
          <span className={classes.phoneLabel}>
            {t('esignature.modal.phone.label')}
          </span>
          <ReactPhoneInput
            value={phone}
            onChange={(value) =>
              setPhone(value[0] === '+' ? value : `+${value}`)
            }
            country={defaultCountry}
            preferredCountries={preferredCountries}
            specialLabel=""
            isValid={() =>
              !phone || valid || t('fields.validators.phone.invalid')
            }
            containerStyle={{ width: '250px' }}
            inputStyle={{
              width: 'calc(100% - 54px)',
              height: '40px',
              marginLeft: '54px',
              padding: '4px 0',
              border: 'none',
              borderBottom: `1px solid ${FO.mediumGrey}`,
              borderRadius: 0,
              fontFamily: FO.font,
              fontSize: '14px',
              color: FO.text,
            }}
            buttonStyle={{
              width: '46px',
              height: '40px',
              border: 'none',
              borderBottom: `1px solid ${FO.mediumGrey}`,
              borderRadius: 0,
            }}
          />
        </div>
        <Button
          className={classes.updateButton}
          disabled={!valid || updating}
          onClick={updatePhone}
        >
          {t('nda.phone.submit')}
        </Button>
        {updatedAt && (
          <span className={classes.updated}>
            <CheckIcon className={classes.updatedIcon} />
            {`${t('esignature.modal.updated.phone')} ${updatedAt}`}
          </span>
        )}
      </div>
      <div className={classes.footer}>
        <Button className={classes.cancel} onClick={handleClose}>
          {t('modal.confirm.cancel')}
        </Button>
        <Button
          className={classes.confirm}
          disabled={!valid || loading}
          onClick={() => handleConfirm(phone)}
        >
          {t(confirmLabel)}
        </Button>
      </div>
    </Dialog>
  );
};

export default PhoneConfirmationModal;
